import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const STATUS_CONFIG = {
  nuevo:      { label: 'Nuevo',      bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500' },
  contactado: { label: 'Contactado', bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500' },
  convertido: { label: 'Convertido', bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500' },
  descartado: { label: 'Descartado', bg: 'bg-gray-100',   text: 'text-gray-500',   dot: 'bg-gray-400' },
};

const STATUS_NEXT = {
  nuevo:      'contactado',
  contactado: 'convertido',
  convertido: null,
  descartado: null,
};

const STATUS_NEXT_LABEL = {
  nuevo:      'Marcar contactado',
  contactado: 'Marcar convertido',
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.nuevo;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function LeadCard({ lead, onStatusChange }) {
  const [updating, setUpdating] = useState(false);
  const next = STATUS_NEXT[lead.status];

  const handleAdvance = async () => {
    if (!next || updating) return;
    setUpdating(true);
    await onStatusChange(lead.id, next);
    setUpdating(false);
  };

  const handleDiscard = async () => {
    if (updating) return;
    setUpdating(true);
    await onStatusChange(lead.id, 'descartado');
    setUpdating(false);
  };

  const date = lead.createdAt
    ? new Date(lead.createdAt).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div className={`bg-white rounded-xl border shadow-sm p-4 flex flex-col gap-3 ${lead.status === 'descartado' ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">{lead.name || 'Sin nombre'}</p>
            <p className="text-xs text-gray-400">{date}</p>
          </div>
        </div>
        <StatusBadge status={lead.status || 'nuevo'} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm">
        {lead.phone && (
          <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-gray-700 hover:text-blue-600">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span className="font-medium">{lead.phone}</span>
          </a>
        )}
        {lead.email && (
          <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-gray-700 hover:text-blue-600 min-w-0">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="truncate">{lead.email}</span>
          </a>
        )}
        {(lead.city || lead.zipCode) && (
          <div className="flex items-center gap-2 text-gray-600">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            </svg>
            <span>{[lead.city, lead.zipCode].filter(Boolean).join(' · ')}</span>
          </div>
        )}
        {lead.service && (
          <div className="flex items-center gap-2 text-gray-600">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="truncate">{lead.service}</span>
          </div>
        )}
      </div>

      {lead.stelOrderIncidentRef && (
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <span>StelOrder: <strong>{lead.stelOrderIncidentRef}</strong></span>
          {lead.isNewClient && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded font-medium">Cliente nuevo</span>}
        </div>
      )}

      {lead.error && (
        <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2 truncate" title={lead.error}>
          Error: {lead.error}
        </p>
      )}

      {lead.status !== 'convertido' && lead.status !== 'descartado' && (
        <div className="flex gap-2 pt-1">
          {next && (
            <button
              onClick={handleAdvance}
              disabled={updating}
              className="flex-1 py-2 px-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
            >
              {updating ? 'Guardando…' : STATUS_NEXT_LABEL[lead.status]}
            </button>
          )}
          <button
            onClick={handleDiscard}
            disabled={updating}
            className="py-2 px-3 border border-gray-200 hover:bg-gray-50 disabled:opacity-50 text-gray-500 text-xs rounded-lg transition-colors"
          >
            Descartar
          </button>
        </div>
      )}
    </div>
  );
}

const FILTERS = [
  { key: 'all',       label: 'Todos' },
  { key: 'nuevo',     label: 'Nuevos' },
  { key: 'contactado',label: 'Contactados' },
  { key: 'convertido',label: 'Convertidos' },
  { key: 'descartado',label: 'Descartados' },
];

export default function LeadsView() {
  const [leads, setLeads]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');

  useEffect(() => {
    // `leads` crece sin fin; el kanban solo gestiona los recientes.
    const q = query(collection(db, 'leads'), orderBy('createdAt', 'desc'), limit(1500));
    const unsub = onSnapshot(q, snap => {
      setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => {
      console.error('Leads snapshot error:', err);
      setLoading(false);
    });
    return unsub;
  }, []);

  const handleStatusChange = useCallback(async (leadId, newStatus) => {
    await updateDoc(doc(db, 'leads', leadId), { status: newStatus });
  }, []);

  const filtered = filter === 'all' ? leads : leads.filter(l => l.status === filter);

  const counts = leads.reduce((acc, l) => {
    acc[l.status || 'nuevo'] = (acc[l.status || 'nuevo'] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leads Facebook Ads</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {leads.length} lead{leads.length !== 1 ? 's' : ''} recibido{leads.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {counts.nuevo > 0 && (
              <span className="px-3 py-1 bg-blue-600 text-white text-sm font-bold rounded-full">
                {counts.nuevo} nuevo{counts.nuevo !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Configuración webhook */}
        <div className="mb-5 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Configuración del webhook</p>
              <p className="text-blue-700">
                URL del webhook:{' '}
                <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-xs">
                  https://[tu-sitio].netlify.app/.netlify/functions/receiveFacebookLead
                </code>
              </p>
              <p className="mt-1 text-blue-700">
                Configura <code className="bg-blue-100 px-1 rounded font-mono text-xs">FACEBOOK_VERIFY_TOKEN</code> y{' '}
                <code className="bg-blue-100 px-1 rounded font-mono text-xs">FACEBOOK_PAGE_ACCESS_TOKEN</code> en Netlify → Environment variables.
              </p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
              {f.key !== 'all' && counts[f.key] ? (
                <span className={`ml-1.5 text-xs ${filter === f.key ? 'text-white/70' : 'text-gray-400'}`}>
                  {counts[f.key]}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <svg className="w-5 h-5 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Cargando leads…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="font-medium">
              {filter === 'all' ? 'No hay leads aún' : `No hay leads en estado "${FILTERS.find(f => f.key === filter)?.label}"`}
            </p>
            {filter === 'all' && (
              <p className="text-sm mt-1">Los leads de Facebook Ads aparecerán aquí automáticamente</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map(lead => (
              <LeadCard key={lead.id} lead={lead} onStatusChange={handleStatusChange} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
